/**
 * screens/Settings/components/ApprovalRulesCard
 * ──────────────────────────────────────────────────────────────────────────
 * WHEN → THEN, as a table an admin can read out loud.
 *
 * Grid from the design: minmax(0,1.3fr) minmax(0,1.4fr) minmax(0,0.7fr) 150px,
 * gap 14px. Each of the first two cells opens with a 9.5px mono eyebrow —
 * "WHEN" / "THEN" — which is what makes a list of conditions readable as rules
 * rather than as settings.
 *
 * Pausing or removing a rule leaves items ALREADY WAITING untouched: they keep
 * the reason they were held for. An approver must never see a queue entry whose
 * justification has evaporated.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Button, DataTable, Eyebrow, StatusPill, type Column } from '../../../components';
import { neutral } from '../../../theme/colors';
import { sans, size as fontSize, leading } from '../../../theme/typography';
import { formatMinor } from '../../../lib/money';
import { sentenceCase } from '../../../lib/format';
import {
  approversSentence,
  RULE_FIELD_LABEL,
  type ApprovalRule,
  type Category,
  type RuleField,
} from '../../../schemas/config';
import SettingsCard from './SettingsCard';

export interface ApprovalRulesCardProps {
  rules: readonly ApprovalRule[];
  categories: readonly Category[];
  baseCurrency: string | null;
  onAdd: () => void;
  onRemove: (rule: ApprovalRule) => void;
}

export function ApprovalRulesCard({
  rules,
  categories,
  baseCurrency,
  onAdd,
  onRemove,
}: ApprovalRulesCardProps) {
  /**
   * The WHEN sentence.
   *
   * The API composes one itself — "Spend over AED 500.00 needs an approval" —
   * and it is the SAME string that lands on `approval.rule_summary` in the
   * queue. Preferring it means Settings and the approver console describe a rule
   * in identical words; the client-side composition below is the fallback for a
   * rule the server did not summarise.
   */
  const whenSentence = (rule: ApprovalRule): string => {
    if (rule.summary) return rule.summary;
    const field = rule.on_field as RuleField;
    const label = RULE_FIELD_LABEL[field] ?? sentenceCase(rule.on_field);
    if (field === 'noReceipt') return label;
    if (field === 'amount') {
      const amount = rule.value_minor
        ? `${baseCurrency ?? ''} ${formatMinor(rule.value_minor, baseCurrency ?? 'AED')}`.trim()
        : '—';
      return `${label} ${amount}`;
    }
    if (field === 'category') {
      const category = categories.find((c) => c.id === rule.value_text);
      return `${label} ${category?.code ?? rule.value_text ?? '—'}`;
    }
    return `${label} ${sentenceCase(rule.value_text ?? '')}`;
  };

  const columns = useMemo<Column<ApprovalRule>[]>(
    () => [
      {
        key: 'when',
        header: '',
        track: { fr: 1.3 },
        stackedLabel: 'When',
        render: (rule) => (
          <View style={styles.cell}>
            <Eyebrow style={styles.eyebrow}>When</Eyebrow>
            <Text style={styles.when}>{whenSentence(rule)}</Text>
          </View>
        ),
      },
      {
        key: 'then',
        header: '',
        track: { fr: 1.4 },
        stackedLabel: 'Then',
        render: (rule) => (
          <View style={styles.cell}>
            <Eyebrow style={styles.eyebrow}>Then</Eyebrow>
            <Text style={styles.then}>{approversSentence(rule.approvers)}</Text>
          </View>
        ),
      },
      {
        key: 'state',
        header: '',
        track: { fr: 0.7 },
        stackedLabel: 'State',
        render: (rule) => (
          <StatusPill
            label={rule.is_active ? 'live' : 'paused'}
            tone={rule.is_active ? 'positive' : 'neutral'}
          />
        ),
      },
      {
        key: 'actions',
        header: '',
        track: { px: 150 },
        align: 'right',
        stackedLabel: '',
        render: (rule) => (
          <View style={styles.actions}>
            <Button label="Remove" size="xs" variant="outline" onPress={() => onRemove(rule)} />
          </View>
        ),
      },
    ],
    // whenSentence closes over `categories` and `baseCurrency`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, baseCurrency, onRemove],
  );

  return (
    <SettingsCard
      title="Approval rules"
      subtitle="Checked on every spend, top to bottom. The strictest match decides how many approvals are needed."
      action={<Button label="Add rule" size="sm" variant="primary" onPress={onAdd} />}
      footNote="Spends under the auto-approve floor are never held, whatever the rules say."
    >
      <DataTable
        columns={columns}
        rows={rules}
        keyExtractor={(rule) => rule.id}
        empty={
          <View style={styles.empty}>
            <Text style={styles.emptyTitle}>No rules yet</Text>
            <Text style={styles.emptyHint}>
              Without a rule every spend routes straight out. Setting the approval threshold
              creates the first one.
            </Text>
          </View>
        }
      />
    </SettingsCard>
  );
}

const styles = StyleSheet.create({
  cell: { minWidth: 0, gap: 3 },
  eyebrow: { fontSize: fontSize.xxs, letterSpacing: 1 },
  when: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  then: { ...sans('400'), fontSize: fontSize.button, color: neutral.ink },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 6 },
  empty: { paddingVertical: 26, paddingHorizontal: 15, alignItems: 'center', gap: 3 },
  emptyTitle: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  emptyHint: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
    textAlign: 'center',
    maxWidth: 460,
  },
});

export default ApprovalRulesCard;
